import React, { useRef, useEffect, useCallback, useState } from 'react';
// @ts-ignore
import { fabric } from 'fabric';
import { FloorLayoutImage, FloorSpace, SpaceObjectData } from '../types/floorLayout';
import { ParkingType } from '../types/parking';
import { getParkingIconByColor } from '../utils/iconMapping';

interface FloorLayoutViewerProps {
  layoutImage?: FloorLayoutImage;
  spaces: FloorSpace[];
  spaceDefaults?: { width: number; height: number };
  parkingTypes?: ParkingType[];
  onSpaceClick?: (spaceId: string) => void;
}

// Car SVG path
const CAR_SVG_PATH = `M 29.395,0 H 17.636 c -3.117,0 -5.643,3.467 -5.643,6.584 v 34.804 c 0,3.116 2.526,5.644 5.643,5.644 h 11.759 c 3.116,0 5.644,-2.527 5.644,-5.644 V 6.584 C 35.037,3.467 32.511,0 29.395,0 Z M 34.05,14.188 v 11.665 l -2.729,0.351 v -4.806 z M 32.618,10.773 c -1.016,3.9 -2.219,8.51 -2.219,8.51 H 16.631 l -2.222,-8.51 c 0,0 8.883,-3.018 18.209,0 z M 15.741,21.713 v 4.492 l -2.73,-0.349 V 14.502 Z M 13.011,37.938 V 27.579 l 2.73,0.343 v 8.196 z M 14.568,40.882 l 2.218,-3.336 h 13.771 l 2.219,3.336 z M 31.321,35.805 v -7.872 l 2.729,-0.355 v 10.048 z`;

const FloorLayoutViewer: React.FC<FloorLayoutViewerProps> = ({
  layoutImage,
  spaces,
  spaceDefaults = { width: 150, height: 75 },
  parkingTypes = [],
  onSpaceClick
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fabricCanvasRef = useRef<fabric.Canvas | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const onSpaceClickRef = useRef(onSpaceClick);
  const [canvasReady, setCanvasReady] = useState(false);

  // Keep the ref updated with the latest callback
  useEffect(() => {
    onSpaceClickRef.current = onSpaceClick;
  }, [onSpaceClick]);

  // Helper function for type-based colors
  const getTypeColor = useCallback((typeName: string, isPremium: boolean, status: string) => {
    // Find the parking type by name
    const parkingType = parkingTypes.find(pt => pt.name === typeName);

    if (parkingType) {
      // Use selectedColor for premium spaces, standardColor for standard spaces
      return isPremium ? (parkingType.selectedColor || '#2196F3') : (parkingType.standardColor || '#4CAF50');
    }

    // Fallback to status-based colors if type not found
    switch (status) {
      case 'AVAILABLE': return '#4CAF50';
      case 'MARKETING HOLD': return '#FFC107';
      case 'SALES HOLD': return '#FF9800';
      case 'CUSTOMER SELECTION': return '#2196F3';
      case 'CUSTOMER SUBMITTED': return '#9C27B0';
      case 'SOLD': return '#F44336';
      default: return '#9E9E9E';
    }
  }, [parkingTypes]);

  // Helper function to get icon path based on type
  const getTypeIconPath = useCallback((typeName: string, isPremium: boolean, status: string): string | null => {
    // If parkingTypes haven't loaded yet, return null to trigger fallback later
    if (!parkingTypes || parkingTypes.length === 0) {
      return null;
    }

    // Find the parking type by name
    const parkingType = parkingTypes.find(pt => pt.name === typeName);

    if (parkingType) {
      // Use selectedImage for premium spaces, standardImage for standard spaces
      const imagePath = isPremium ? parkingType.selectedImage : parkingType.standardImage;

      // If image path exists, return it
      if (imagePath) {
        return imagePath;
      }

      // Fallback: try to get image from color mapping (for backward compatibility)
      const color = isPremium ? (parkingType.selectedColor || '#2196F3') : (parkingType.standardColor || '#4CAF50');
      return getParkingIconByColor(color);
    }

    return null;
  }, [parkingTypes]);

  // Initialize canvas
  useEffect(() => {
    if (!canvasRef.current || fabricCanvasRef.current) return;

    const container = containerRef.current;
    if (!container) return;

    // Use fixed dimensions to ensure consistency between editor and viewer
    const canvasWidth = 1200;
    const canvasHeight = 800;

    try {
      const canvas = new fabric.Canvas(canvasRef.current, {
        width: canvasWidth,
        height: canvasHeight,
        backgroundColor: '#f5f5f5',
        selection: false, // Disable selection in viewer mode
      });

      fabricCanvasRef.current = canvas;
      setCanvasReady(true);
    } catch (error) {
      console.error('FloorLayoutViewer: Error initializing canvas:', error);
    }

    // Cleanup
    return () => {
      if (fabricCanvasRef.current) {
        fabricCanvasRef.current.dispose();
        fabricCanvasRef.current = null;
      }
      setCanvasReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load background image
  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || !canvasReady) return;

    if (layoutImage?.url) {
      fabric.Image.fromURL(layoutImage.url, (img: any) => {
        if (!img || !canvas.getContext()) return;

        const scale = Math.min(
          (canvas.width || 1) / (img.width || 1),
          (canvas.height || 1) / (img.height || 1)
        ) * 0.9;

        img.scale(scale);
        img.set({
          left: ((canvas.width || 0) - (img.width || 0) * scale) / 2,
          top: ((canvas.height || 0) - (img.height || 0) * scale) / 2,
          selectable: false,
          evented: false,
        });

        canvas.setBackgroundImage(img, () => {
          canvas.renderAll();
        });
      });
    } else {
      // Clear background if no image
      canvas.setBackgroundImage(null as any, () => {
        canvas.renderAll();
      });
    }
  }, [layoutImage, canvasReady]);

  // Render spaces
  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || !canvasReady) return;

    // Clear existing space objects (keep background)
    const objectsToRemove = canvas.getObjects().filter((obj: any) => obj.spaceId);
    objectsToRemove.forEach(obj => canvas.remove(obj));

    // Add space objects
    spaces.forEach(space => {
      const position = space.spaceObject as SpaceObjectData;
      if (!position) return;

      const width = position.width || spaceDefaults.width;
      const height = position.height || spaceDefaults.height;

      // Create addon color dots if addons exist
      // Combine both regular addons and mandatory addons
      const allAddons = [...(space.addons || []), ...(space.mandatoryAddons || [])];
      const addonDots: fabric.Circle[] = [];
      if (allAddons.length > 0) {
        const dotRadius = 4;
        const dotSpacing = 10;
        const totalAddons = allAddons.length;

        allAddons.forEach((addon, index) => {
          if (addon.color) {
            const dot = new fabric.Circle({
              radius: dotRadius,
              fill: addon.color,
              stroke: '#ffffff',
              strokeWidth: 1.5,
              // Position dots at top-middle, stacked horizontally
              left: -((totalAddons - 1) * dotSpacing / 2) + (index * dotSpacing),
              top: -(height / 2) - dotRadius - 1,
              originX: 'center',
              originY: 'center',
              selectable: false,
              evented: false,
            });
            addonDots.push(dot);
          }
        });
      }

      // Helper function to create group with shape
      const createGroupWithShape = (shapeElement: fabric.Object) => {
        (shapeElement as any).spaceId = space._id;

        // Group shape and addon dots, then position the group (no text label in viewer mode)
        const groupItems: any[] = [...addonDots, shapeElement];
        const group = new fabric.Group(groupItems, {
          left: position.left || 0,
          top: position.top || 0,
          angle: position.angle || 0,
          originX: 'center',
          originY: 'center',
          selectable: false,
          evented: !!onSpaceClickRef.current,
          hoverCursor: 'pointer',
        });

        (group as any).spaceId = space._id;
        (group as any).spaceNumber = space.number;

        // Handle click using ref to avoid re-renders
        group.on('mousedown', () => {
          if (onSpaceClickRef.current) {
            onSpaceClickRef.current(space._id);
          }
        });

        canvas.add(group);
        canvas.renderAll();
      };

      // Get icon path for this space
      const iconPath = getTypeIconPath(space.type, space.isPremium || false, space.status);

      // If we have an icon path, load the image
      if (iconPath) {
        fabric.Image.fromURL(iconPath, (img: fabric.Image) => {
          if (img && img.width && img.height) {
            const scale = Math.min(width / img.width, height / img.height);
            img.set({
              scaleX: scale,
              scaleY: scale,
              left: 0,
              top: 0,
              originX: 'center',
              originY: 'center',
              selectable: false,
              evented: false,
            });
            createGroupWithShape(img);
          } else {
            // Fallback to SVG path if image fails to load
            const path = new fabric.Path(CAR_SVG_PATH, {
              fill: getTypeColor(space.type, space.isPremium || false, space.status),
              stroke: '#000000',
              strokeWidth: 1,
              scaleX: width / 47.032,
              scaleY: height / 47.032,
              left: 0,
              top: 0,
              originX: 'center',
              originY: 'center',
              selectable: false,
              evented: false,
            });
            createGroupWithShape(path);
          }
        }, { crossOrigin: 'anonymous' });
      } else {
        // Fallback to SVG path if no icon path found
        const path = new fabric.Path(CAR_SVG_PATH, {
          fill: getTypeColor(space.type, space.isPremium || false, space.status),
          stroke: '#000000',
          strokeWidth: 1,
          scaleX: width / 47.032,
          scaleY: height / 47.032,
          left: 0,
          top: 0,
          originX: 'center',
          originY: 'center',
          selectable: false,
          evented: false,
        });
        createGroupWithShape(path);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spaces, spaceDefaults, canvasReady, getTypeColor, getTypeIconPath, parkingTypes]);

  return (
    <div ref={containerRef} style={{ width: '100%', minHeight: '600px', position: 'relative', overflow: 'auto' }}>
      {!canvasReady ? (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          color: '#6B7280',
          fontSize: '14px',
          zIndex: 10
        }}>
          Loading canvas...
        </div>
      ) : !layoutImage && spaces.length === 0 ? (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          color: '#6B7280',
          fontSize: '14px',
          zIndex: 10
        }}>
          <p>No layout image uploaded</p>
          <p style={{ fontSize: '12px', marginTop: '8px' }}>Upload a layout image to get started</p>
        </div>
      ) : null}
      <canvas ref={canvasRef} />
    </div>
  );
};

export default FloorLayoutViewer;
